import { calendarEra, formatCount, isGregorian, monthName, monthNames, parseCount, partsToMs, type TimeUnit } from '@dispositio/core';

/**
 * Datum und Uhrzeit im 24-Stunden-Format, unabhängig von der Spracheinstellung des Browsers (das eingebaute Datumsfeld zeigt
 * je nach System AM/PM). Gespeichert wird weiter `JJJJ-MM-TTThh:mm`. In einem eigenen Kalender (PF 16) mit Monatsnamen.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** `JJJJ-MM-TTThh:mm` → „TT.MM.JJJJ hh:mm“, im eigenen Kalender „12. Frostmond 312 n. F. 08:00“ (leer bleibt leer, Unbekanntes bleibt unverändert). */
export function toDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(iso.trim());
  if (!m) return iso;
  const time = `${m[4] ?? '00'}:${m[5] ?? '00'}`;
  if (!isGregorian()) return `${Number(m[3])}. ${monthName(Number(m[2]))} ${Number(m[1])}${calendarEra() ? ` ${calendarEra()}` : ''} ${time}`;
  return `${m[3]}.${m[2]}.${m[1]} ${time}`;
}

const valid = (y: number, mo: number, d: number, h: number, mi: number): boolean => {
  if (!isGregorian()) return partsToMs({ y, mo, d, h, mi }) !== null;
  if (mo < 1 || mo > 12 || h > 23 || mi > 59) return false;
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d >= 1 && d <= days;
};

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Eingabe → `JJJJ-MM-TTThh:mm`. Erlaubt „TT.MM.JJJJ hh:mm“ (auch einstellige Tage, Monate, Stunden, „8.30“ oder „8 Uhr“ nicht),
 * nur das Datum (Uhrzeit dann 00:00) und die gespeicherte Form; im eigenen Kalender auch mit Monatsnamen („12. Frostmond 312 08:00“,
 * die Jahreszählung darf dabeistehen) und Jahre mit weniger als vier Ziffern. Leer → '' ; ungültig → null.
 */
export function parseDateTime(input: string): string | null {
  const s = input.trim();
  if (s === '') return '';
  const own = !isGregorian();
  const era = own && calendarEra() ? `(?:\\s+${escapeRe(calendarEra())})?` : '';
  const clock = '(?:[ ,T]+(\\d{1,2})[:.](\\d{2}))?';
  let m = new RegExp(`^(\\d{1,2})\\.(\\d{1,2})\\.(\\d{${own ? '1,4' : '4'}})${era}${clock}$`).exec(s);
  let y: number, mo: number, d: number, h: number, mi: number;
  if (m) {
    [d, mo, y, h, mi] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0)];
  } else if (own && (m = new RegExp(`^(\\d{1,2})\\.\\s*(${monthNames().map(escapeRe).join('|')})\\s+(\\d{1,4})${era}${clock}$`, 'iu').exec(s))) {
    const name = m[2]!.toLowerCase();
    [d, mo, y, h, mi] = [Number(m[1]), monthNames().findIndex((n) => n.toLowerCase() === name) + 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0)];
  } else {
    m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/.exec(s);
    if (!m) return null;
    [y, mo, d, h, mi] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0)];
  }
  if (!valid(y, mo, d, h, mi)) return null;
  return `${String(y).padStart(4, '0')}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

/**
 * Eingabe einer Zeit → gespeicherte Form: Datum und Uhrzeit (siehe `parseDateTime`) oder ein Wert einer eigenen Zählung („Tag 12,
 * 06:00“, „Minute 45“, „Jahr 340, Tag 120“); eine bloße Zahl zählt in der Einheit `unit`. Leer → ''; ungültig → null.
 */
export function parseTime(input: string, unit?: TimeUnit): string | null {
  const date = parseDateTime(input);
  if (date !== null) return date;
  const c = parseCount(input, unit);
  return c ? formatCount(c.unit, c.value) : null;
}
